import React, {useRef} from 'react';
import {useTranslation} from "react-i18next";
import {FormField as FormFieldType, FormData, FormFiles, FormErrors} from '../../../../../types/ContactForm';
import {
    getEffectiveMaxTotalFileSize,
    getTotalFileSizeBytes,
    hasPerFileSizeError
} from '../../../../../helpers/fileFieldLimits';

interface FileFieldProps {
    field: FormFieldType;
    formData: FormData;
    files: FormFiles;
    errors: FormErrors;
    inputCssClass?: string;
    onFileChange: (name: string, file: File | File[] | null) => void;
    onFieldFocus: (fieldName: string) => void;
}

const getFilesForField = (files: FormFiles, fieldName: string): File[] => {
    const value = files[fieldName];
    if (!value) {
        return [];
    }
    return Array.isArray(value) ? value : [value];
};

const getAcceptAttribute = (allowedFileTypes?: string): string => {
    if (!allowedFileTypes || allowedFileTypes.trim() === '') {
        return '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png';
    }

    return allowedFileTypes
        .split(',')
        .map(ext => ext.trim())
        .filter(ext => ext)
        .map(ext => ext.startsWith('.') ? ext : `.${ext}`)
        .join(',');
};

const isSameFile = (a: File, b: File): boolean =>
    a.name === b.name && a.size === b.size && a.lastModified === b.lastModified;

const dedupeIncoming = (existing: File[], incoming: File[]): File[] =>
    incoming.filter((file) => !existing.some((current) => isSameFile(current, file)));

export const FileField: React.FC<FileFieldProps> = ({
                                                        field,
                                                        files,
                                                        errors,
                                                        onFileChange,
                                                        onFieldFocus
                                                    }) => {
    const {t} = useTranslation('form');
    const inputRef = useRef<HTMLInputElement>(null);
    const fileMaxSize = field.maxFileSize || 5;
    const maxFiles = field.maxFiles || 5;
    const maxSizeBytes = fileMaxSize * 1024 * 1024;
    const currentFiles = getFilesForField(files, field.name);
    const hasPerFileError = hasPerFileSizeError(field, currentFiles);
    const effectiveMaxTotal = getEffectiveMaxTotalFileSize(field);
    const totalSizeBytes = getTotalFileSizeBytes(currentFiles);
    const hasTotalSizeError = effectiveMaxTotal !== null
        && currentFiles.length > 0
        && totalSizeBytes > effectiveMaxTotal * 1024 * 1024;
    const hasCustomTypes = Boolean(field.allowedFileTypes?.trim());
    const isAtMaxFiles = field.allowMultipleFiles && currentFiles.length >= maxFiles;

    const handleFileChange = (selectedFiles: FileList | null) => {
        if (!selectedFiles || selectedFiles.length === 0) {
            return;
        }

        const incoming = Array.from(selectedFiles);

        if (field.allowMultipleFiles) {
            const uniqueIncoming = dedupeIncoming(currentFiles, incoming);
            const remainingSlots = maxFiles - currentFiles.length;
            const filesToAdd = uniqueIncoming.slice(0, remainingSlots);
            const merged = [...currentFiles, ...filesToAdd];
            onFileChange(field.name, merged.length > 0 ? merged : null);
        } else {
            onFileChange(field.name, incoming[0] ?? null);
        }

        if (inputRef.current) {
            inputRef.current.value = '';
        }
        onFieldFocus(field.name);
    };

    const handleRemoveFile = (index: number) => {
        const updated = currentFiles.filter((_, i) => i !== index);
        onFileChange(field.name, updated.length > 0 ? updated : null);
        onFieldFocus(field.name);
    };

    const getHelpText = (): string => {
        const maxTotalFileSize = effectiveMaxTotal ?? maxFiles * fileMaxSize;

        if (field.allowMultipleFiles) {
            if (hasCustomTypes) {
                return t('multipleFilesAllowedWithTypes', {
                    types: field.allowedFileTypes,
                    maxFiles,
                    fileMaxSize,
                    maxTotalFileSize
                });
            }
            return t('multipleFilesAllowed', {maxFiles, fileMaxSize, maxTotalFileSize});
        }

        if (hasCustomTypes) {
            return t('allowedFileTypes', {types: field.allowedFileTypes, fileMaxSize});
        }

        return t('allowedFiles', {fileMaxSize});
    };

    const formatFileSize = (size: number) => (size / (1024 * 1024)).toFixed(2);
    const totalSizeMB = formatFileSize(totalSizeBytes);
    const isRequired = field.required && (!field.allowMultipleFiles || currentFiles.length === 0);

    return (
        <div className={`col-12 col-md-${field.width || 12}`}>
            <div
                className={`form-group ${field.required ? 'required' : ''} ${errors[field.name] || hasPerFileError || hasTotalSizeError ? 'has-error' : ''}`}>
                <label htmlFor={`field-${field.id}`} className="control-label">
                    {field.label}
                </label>
                <input
                    ref={inputRef}
                    type="file"
                    id={`field-${field.id}`}
                    name={field.name}
                    className="form-control"
                    accept={getAcceptAttribute(field.allowedFileTypes)}
                    multiple={field.allowMultipleFiles || false}
                    disabled={isAtMaxFiles}
                    onChange={(e) => handleFileChange(e.target.files)}
                    required={isRequired}
                    aria-required={field.required}
                />

                {isAtMaxFiles && (
                    <small className="form-text text-muted">
                        {t('maxFilesReached', {maxFiles})}
                    </small>
                )}

                {currentFiles.length > 0 && (
                    <div className="form-text mt-1 file-field-list">
                        {currentFiles.map((file, index) => {
                            const isOversized = file.size > maxSizeBytes;
                            return (
                                <div
                                    key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
                                    className={`file-field-list-item d-flex align-items-center justify-content-between ${isOversized ? 'text-danger' : 'text-muted'}`}>
                                    <div className="d-flex align-items-center">
                                        <i className="fas fa-file me-1"></i>
                                        <span>{file.name} ({formatFileSize(file.size)}MB)</span>
                                        {isOversized && (
                                            <i className="fa fa-exclamation-triangle ms-2" title="Datei zu groß"></i>
                                        )}
                                    </div>
                                    {field.allowMultipleFiles && (
                                        <button
                                            type="button"
                                            className="file-field-remove"
                                            onClick={() => handleRemoveFile(index)}
                                            aria-label={t('removeFile')}
                                        >
                                            <i className="fa fa-times"></i>
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {hasPerFileError && (
                    <div className="form-error-msg text-danger">
                        <i className="fa fa-warning"></i> {t('maxSize', {fileMaxSize})}
                    </div>
                )}

                {hasTotalSizeError && effectiveMaxTotal !== null && (
                    <div className="form-error-msg text-danger">
                        <i className="fa fa-warning"></i> {t('totalFileSizeExceeded', {
                            maxTotal: effectiveMaxTotal,
                            actual: totalSizeMB
                        })}
                    </div>
                )}

                <small className="form-text text-muted">
                    {getHelpText()}
                </small>

                {errors[field.name] && !hasPerFileError && !hasTotalSizeError && (
                    <div className="form-error-msg text-danger">
                        <i className="fa fa-warning"></i> {errors[field.name]}
                    </div>
                )}
            </div>
        </div>
    );
};

